/**
 * 작성자 : 홍선영
 * 날짜 : 2024.11.21
 * 경로 : (사내)업무관리 - 일정관리
 */

import React, { RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { SearchOptions } from '../../../assets/styles/SearchOptions';
import { BtnGhost, InitBtn } from '../../../components/Button';
import Input from '../../../components/Input';
import SelectBox from '../../../components/SelectBox';
import SelectBoxMulti from '../../../components/SelectBoxMulti';
import ShortcutButton from '../../../components/button/ShortcutButton';
import BigCalendar from '../../../components/BigCalendar';
import GetFetchStatus from '../../../hooks/GetFetchStatus';
import { useRecoilValue } from 'recoil';
import { IUser, userState } from '../../../atoms';
import { ISelectBox } from 'customTypes';
import { ContentsContainerRoot } from '../../../assets/styles/ContentsContainerRoot';
import { PulseLoader } from 'react-spinners';
import { PROGRESS_TYPE_LIST, SCHEDULE_TYPE_LIST } from '../../../_constants';
import { useSetAuth } from '../../../utils/useSetAuth';
import { useOutletContext } from 'react-router-dom';
import Portal from '../../../components/Portal';
import SearchFilterModal from '../../../components/Modal/SearchFilterModal';
import { useCalendar } from '../../../hooks/useCalendar';
import dayjs from 'dayjs';
import SearchSelectBoxSm from '../../../components/SearchSelectBoxSm';
import { isMobile } from '../../../utils/checkMobile';
import { logPost } from '../../../services/log';

type CalendarView = 'month' | 'week' | 'work_week' | 'day' | 'agenda' | 'twoWeeks' | 'threeWeeks';

const Root = styled(ContentsContainerRoot)`
  .filter-wrapper {
    padding: 0 0 0.5rem 0;
  }
`;

const isMobileDevice = isMobile();

const Schedule = () => {
  const userInfo = useRecoilValue<IUser>(userState);
  const { t } = useTranslation();
  const size = useOutletContext<any>();
  const INIT_SCHEDULE_TYPE = { type: 'scheduleType', scheduleType: 'A', cdName: t('전체') };
  const INIT_SHARE_TYPE = { type: 'csCd', csCd: 'A', cdName: t('전체'), backColor: '', fontColor: '' };
  const INIT_PROGRESS_TYPE = { type: 'progressType', progressType: 'A', cdName: t('전체') };
  const [scheduleType, setScheduleType] = useState(INIT_SCHEDULE_TYPE);
  const [progressType, setProgressType] = useState(INIT_PROGRESS_TYPE);
  const [csCdList, setCsCdList] = useState<ISelectBox[]>([INIT_SHARE_TYPE]);
  const [appliedCsCdList, setAppliedCsCdList] = useState<ISelectBox[]>([INIT_SHARE_TYPE]);

  const [openModal, setOpenModal] = useState<any>({ status: false, type: '' });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState<CalendarView>('month');
  const [searchOption, setSearchOption] = useState({ search: '' });
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const searchRequestPendingRef = useRef(false);
  const hasViewInitRef = useRef(true);
  const [shouldRefetchAfterInit, setShouldRefetchAfterInit] = useState(false);
  const { data: scheduleSets, isLoading: isLoadingScheduleSet } = GetFetchStatus({
    path: '/schedule/set',
    req: { cCd: userInfo.hCd },
    useLoginUserInfo: false,
  });
  const [scheduleSetList, setScheduleSetList] = useState<ISelectBox[] | null>(scheduleSets || null);
  const { auth } = useSetAuth();
  const [hsCd1, setHsCd1] = useState({ type: 'hsCd1', hsCd1: '', cdName: t('본사 선택') });

  const getCalendarDateRange = (date: Date, view: CalendarView) => {
    const d = dayjs(date);

    // 2주/3주: 해당 주 일요일부터 N주
    if (view === 'twoWeeks' || view === 'threeWeeks') {
      const start = d.startOf('week');
      const weeks = view === 'twoWeeks' ? 2 : 3;
      const end = start.add(weeks * 7 - 1, 'day');
      return {
        date1: start.format('YYYYMMDD'),
        date2: end.format('YYYYMMDD'),
      };
    }

    // 1주: 화면에 보이는 주(일~토) 전체 (전월/익월에 걸친 부분 포함)
    if (view === 'week') {
      return {
        date1: d.startOf('week').format('YYYYMMDD'),
        date2: d.endOf('week').format('YYYYMMDD'),
      };
    }

    // 일별: 해당 일
    if (view === 'day') {
      return {
        date1: d.format('YYYYMMDD'),
        date2: d.format('YYYYMMDD'),
      };
    }

    // 목록(agenda): 기존과 동일하게 당월 범위 유지
    if (view === 'agenda') {
      return {
        date1: d.startOf('month').format('YYYYMMDD'),
        date2: d.endOf('month').format('YYYYMMDD'),
      };
    }

    // 월별: 화면에 실제로 보이는 그리드 전체 (첫 주의 전월 말일 ~ 마지막 주의 익월 초 포함)
    return {
      date1: d.startOf('month').startOf('week').format('YYYYMMDD'),
      date2: d.endOf('month').endOf('week').format('YYYYMMDD'),
    };
  };

  const { date1, date2 } = getCalendarDateRange(currentDate, calendarView);

  const req = {
    cCd: userInfo.hCd,
    date1,
    date2,
    smGubun: scheduleType.scheduleType !== 'A' ? scheduleType.scheduleType : '',
    // csCd: csCd.csCd !== 'A' ? csCd.csCd : '',
    completeYn: progressType.progressType !== 'A' ? progressType.progressType : '',
    hCd: `${hsCd1.hsCd1}`,
    search: searchOption.search?.trim(),
  };

  const {
    scheduleList,
    isLoading: isLoadingSchedules,
    refetchSchedule,
    queryClient,
    headInfoList,
    scheduleInfoList,
    companyUser,
    userPartList,
    carInfoList,
    isLoading,
    isRefetchingSchedules,
  } = useCalendar({ enabled: true, currentDate, setCurrentDate, auth, searchOption: req });

  useEffect(() => {
    if (scheduleSets) {
      const newArray: ISelectBox[] = scheduleSets
        .filter((cs: any) => cs.useYn === 'Y')
        .map((el: any) => ({ type: 'csCd', csCd: el.csCd, cdName: el.csName, backColor: el.backColor, fontColor: el.fontColor }));
      setScheduleSetList([INIT_SHARE_TYPE, ...newArray]);
    }
  }, [scheduleSets]);

  const onClickSearch = async (filterRef?: RefObject<HTMLDivElement>) => {
    if (isRefetchingSchedules || searchRequestPendingRef.current) return;
    searchRequestPendingRef.current = true;
    // 검색 버튼 클릭 시 현재 선택된 공유항목 리스트 적용
    setAppliedCsCdList(csCdList);

    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '일정관리(캘린더)',
      action: '조회',
      etc: `${scheduleType.scheduleType !== 'A' ? `일정구분 : ${scheduleType.cdName}` : ''} 
      ${scheduleType.scheduleType === '3' ? `공유항목 : ${csCdList.map((item) => item.cdName).join(',')}` : ''} 
      ${progressType.progressType !== 'A' ? `완료여부 : ${progressType.cdName}` : ''} 
      ${hsCd1.hsCd1 !== '' ? `본사 : ${hsCd1.cdName}` : ''} 
      ${searchOption.search !== '' ? `검색어 : ${searchOption.search}` : ''}`,
    });
    if (filterRef) {
      // 모바일일 경우 검색 필터 모달 창 닫기
      filterRef.current?.classList.remove('active');
      setOpenModal({ status: false, type: 'filter' });
    }
    try {
      await refetchSchedule();
    } finally {
      searchRequestPendingRef.current = false;
    }
  };

  const onEnterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onClickSearch();
    }
  };

  const onClickInitiateSearchOption = () => {
    setScheduleType(INIT_SCHEDULE_TYPE);
    setProgressType(INIT_PROGRESS_TYPE);
    setSearchOption({ search: '' });
    setCsCdList([INIT_SHARE_TYPE]);
    setAppliedCsCdList([INIT_SHARE_TYPE]);
    setHsCd1({ type: 'hsCd1', hsCd1: '', cdName: t('본사 선택') });
    setShouldRefetchAfterInit(true);
  };

  // 초기화 후 refetch 실행
  useEffect(() => {
    if (!isMobileDevice && shouldRefetchAfterInit) {
      refetchSchedule();
      setShouldRefetchAfterInit(false);
    }
  }, [shouldRefetchAfterInit, refetchSchedule]);

  useEffect(() => {
    if (hasViewInitRef.current) {
      hasViewInitRef.current = false;
      return;
    }
    refetchSchedule();
  }, [calendarView, refetchSchedule]);

  // 필터 버튼 클릭
  const onClickFilter = () => {
    setOpenModal({ status: true, type: 'filter', buttonRef: filterButtonRef });
  };

  useEffect(() => {
    // 일정구분 선택값을 변경하는 경우 공유항목 선택값 초기화
    if (scheduleType.scheduleType !== '3') {
      setCsCdList([INIT_SHARE_TYPE]);
      setAppliedCsCdList([INIT_SHARE_TYPE]);
    }
  }, [scheduleType.scheduleType]);

  const loading = (isLoadingSchedules && isLoadingScheduleSet && isLoading) || scheduleSetList === null || scheduleList === null;

  // appliedCsCdList에 따라 일정 필터링 (검색 버튼 클릭 시 적용된 값 사용)
  const filteredScheduleList = React.useMemo(() => {
    if (!scheduleList) return [];

    // 전체('A')가 선택되어 있으면 필터링하지 않음
    const hasAllOption = appliedCsCdList.some((item) => item.csCd === 'A' || item.csCd === '');
    if (hasAllOption) {
      return scheduleList;
    }

    // 선택된 csCd 값들 추출
    const selectedCsCds = appliedCsCdList.map((item) => item.csCd).filter((csCd) => csCd && csCd !== 'A' && csCd !== '');

    // csCd가 선택된 값 중 하나와 일치하는 일정만 필터링
    return scheduleList.filter((event: any) => {
      return selectedCsCds.includes(event.csCd);
    });
  }, [scheduleList, appliedCsCdList]);

  return (
    <div className='content-container oneColumn'>
      <Root loading={loading}>
        {loading ? (
          <div className='centered-content'>
            <PulseLoader color='rgb(0, 122, 255)' size='10px' />
          </div>
        ) : (
          <div>
            <SearchOptions>
              {size.innerSize.W > 1024 ? (
                <div className='inputsWrapper'>
                  <div className='inputForm-row'>
                    <div className='inputForm-col'>
                      <Input placeholder={t('통합검색')} label='' type='text' id='search' name='search' state={searchOption} setState={setSearchOption} onKeyDown={onEnterKeyDown} clearable />
                    </div>
                  </div>
                  <BtnGhost ref={filterButtonRef} onClick={onClickFilter} className='filter-btn'>
                    <span className='material-symbols-rounded'>filter_alt</span>
                  </BtnGhost>
                  <ShortcutButton icon='search' buttonText='검색' shortcut='' onClick={onClickSearch} disabled={isRefetchingSchedules} />
                  <InitBtn onClick={onClickInitiateSearchOption}>{t('초기화')}</InitBtn>
                </div>
              ) : (
                <div className='filter-wrapper'>
                  <div className='inputsWrapper'>
                    <div className='inputForm-row' style={{ width: 'calc(100% - 3.5rem)' }}>
                      <div className='inputForm-col'>
                        <Input placeholder={t('검색')} label='' type='search' id='search' name='search' state={searchOption} setState={setSearchOption} onKeyDown={onEnterKeyDown} ico='search' />
                      </div>
                    </div>
                    <BtnGhost onClick={onClickFilter} className='filter-btn'>
                      <span className='material-symbols-rounded'>filter_alt</span>
                    </BtnGhost>
                  </div>
                </div>
              )}
            </SearchOptions>
            <BigCalendar
              events={filteredScheduleList}
              refetch={refetchSchedule}
              queryClient={queryClient}
              currentDate={currentDate}
              setCurrentDate={setCurrentDate}
              onViewChange={setCalendarView}
              auth={auth}
              isSearchSmGubunAll={scheduleType.scheduleType === 'A'} // 일정구분 검색 선택값이 '전체'인 지 여부
              headInfoList={headInfoList}
              scheduleInfoList={scheduleInfoList}
              userPartList={userPartList}
              carInfoList={carInfoList}
              companyUser={companyUser}
              hideOptionalToolBar={false}
              isLoading={isRefetchingSchedules}
            />
          </div>
        )}
      </Root>

      <Portal openModal>
        <SearchFilterModal openModal={openModal} setOpenModal={setOpenModal} onClickSearch={onClickSearch} onClickReset={onClickInitiateSearchOption} isSearching={isRefetchingSchedules}>
          <div className='filter-option-wrapper'>
            <div className='filter-option-cont'>
              <div className='filter-option-row'>
                <label htmlFor='progressType'>{t('본사')}</label>
                <div className='inputForm-col'>
                  <SearchSelectBoxSm
                    options={headInfoList || []}
                    defaultOption={hsCd1.cdName || t('본사 선택')}
                    state={hsCd1}
                    setState={setHsCd1}
                    stateKey='hsCd1'
                    codeKey='cdName'
                    initiateKey={hsCd1.hsCd1}
                    optionHeight='height-sm'
                    filterbar='filter-1-center'
                  />
                </div>
              </div>
              <div className='filter-option-row'>
                <label htmlFor='scheduleType'>{t('일정구분')}</label>
                <div className='inputForm-col'>
                  <SelectBox
                    options={SCHEDULE_TYPE_LIST}
                    defaultOption={t('전체')}
                    state={scheduleType}
                    setState={setScheduleType}
                    stateKey='scheduleType'
                    initiateKey={scheduleType.scheduleType}
                    filterbar='filter-1-left'
                    optionHeight='height-md'
                  />
                </div>
              </div>
              <div className='filter-option-row'>
                <label htmlFor='progressType'>{t('공유항목')}</label>
                <div className='inputForm-col'>
                  <SelectBoxMulti
                    options={scheduleSetList}
                    defaultOption={t('전체')}
                    state={csCdList}
                    setState={setCsCdList}
                    stateKey='csCd'
                    codeKey='cdName'
                    filterbar='filter-1-left'
                    optionHeight='height-lg'
                    clickAble={scheduleType.scheduleType !== '3'}
                    toastContent={t('일정구분을 일반으로 선택하신 후에 선택해 주세요')}
                    optionType='color'
                  />
                </div>
              </div>
              <div className='filter-option-row'>
                <label htmlFor='progressType'>{t('완료여부')}</label>
                <div className='inputForm-col'>
                  <SelectBox
                    options={PROGRESS_TYPE_LIST}
                    defaultOption={t('전체')}
                    state={progressType}
                    setState={setProgressType}
                    stateKey='progressType'
                    initiateKey={progressType.progressType}
                    filterbar='filter-1-left'
                    optionHeight='height-md'
                  />
                </div>
              </div>
            </div>
          </div>
        </SearchFilterModal>
      </Portal>
    </div>
  );
};

export default Schedule;
